// Port of the value-encoding helpers scattered over webapp/src/properties/*
// and cardFilter.createDatePropertyFromString().
//
// Verified encodings:
//   date      -> plain millisecond string, or JSON {from, to, includeTime}
//   select    -> option id      multiSelect -> JSON array of option ids
//   checkbox  -> "true"
//   number    -> digits string  person -> user id  multiPerson -> JSON array

import {IPropertyOption, IPropertyTemplate, PropertyTypeEnum} from '../types/board'
import {Card} from '../types/card'
import {Constants} from '../common/constants'
import {Utils} from '../common/utils'

export type DateProperty = {
    from?: number
    to?: number
    includeTime?: boolean
    timeZone?: string
}

export function createDatePropertyFromString(initialValue: string | string[] | undefined): DateProperty {
    let dateProperty: DateProperty = {}
    const value = typeof initialValue === 'string' ? initialValue : ''
    if (value) {
        const singleDate = new Date(Number(value))
        if (!Number.isNaN(singleDate.getTime())) {
            dateProperty = {from: singleDate.getTime()}
        } else {
            try {
                dateProperty = JSON.parse(value) as DateProperty
            } catch {
                dateProperty = {}
            }
        }
    }
    return dateProperty
}

export function encodeDateProperty(property: DateProperty): string {
    return property.from || property.to ? JSON.stringify(property) : ''
}

export function todayValue(offsetDays = 0): string {
    const d = new Date()
    d.setHours(12, 0, 0, 0)
    return encodeDateProperty({from: d.getTime() + offsetDays * 86400000, to: undefined})
}

export function parseArrayValue(value: string | string[] | undefined): string[] {
    if (Array.isArray(value)) {
        return value
    }
    if (!value) {
        return []
    }
    if (value.startsWith('[')) {
        try {
            return JSON.parse(value) as string[]
        } catch {
            return [value]
        }
    }
    return value ? [value] : []
}

export function encodeArrayValue(values: string[]): string {
    return values.length ? JSON.stringify(values) : ''
}

/** CardDetailProperties / table cells / chips all funnel through here. */
export function displayPropertyValue(
    value: string | string[] | undefined,
    template: IPropertyTemplate | undefined,
    usersById: Record<string, {username: string}>,
): string {
    if (template) {
        switch (template.type) {
        case 'select': {
            const optionId = typeof value === 'string' ? value : ''
            return template.options.find((o) => o.id === optionId)?.value || ''
        }
        case 'multiSelect':
            return parseArrayValue(value).
                map((id) => template.options.find((o) => o.id === id)?.value || '').
                filter((v) => Boolean(v)).
                join(', ')
        case 'person':
            return value ? usersById[value as string]?.username || '' : ''
        case 'multiPerson':
            return parseArrayValue(value).map((id) => usersById[id]?.username || '').join(', ')
        case 'date': {
            const dp = createDatePropertyFromString(value)
            if (!dp.from && !dp.to) {
                return ''
            }
            if (dp.from && dp.to && dp.from !== dp.to) {
                return `${Utils.displayDate(new Date(dp.from))} 至 ${Utils.displayDate(new Date(dp.to))}`
            }
            return Utils.displayDate(new Date(dp.from || dp.to!))
        }
        case 'checkbox':
            return value === 'true' ? '是' : ''
        case 'createdTime':
            return ''
        default:
            break
        }
    }
    return typeof value === 'string' ? value : (value || []).join(', ')
}

export function dateValueOf(value: string | string[] | undefined): Date | undefined {
    const dp = createDatePropertyFromString(value)
    const ms = dp.from || dp.to
    return ms ? new Date(ms) : undefined
}

/** The `propColorXxx` token on an option maps to --prop-xxx. */
export function optionColorCssVar(color: string | undefined): string {
    if (!color || color === 'propColorDefault') {
        return 'var(--prop-default)'
    }
    const suffix = color.replace(/^propColor/, '').toLowerCase()
    return `var(--prop-${suffix})`
}

export function optionOf(template: IPropertyTemplate | undefined, optionId: string | undefined): IPropertyOption | undefined {
    if (!template || !optionId) {
        return undefined
    }
    return template.options.find((o) => o.id === optionId)
}

/** The property types a user can create, mirroring the "Add property" menus. */
export const addablePropertyTypes: Array<{type: PropertyTypeEnum, label: string}> = [
    {type: 'select', label: '单选'},
    {type: 'multiSelect', label: '多选'},
    {type: 'person', label: '人员'},
    {type: 'multiPerson', label: '人员'},
    {type: 'number', label: '数字'},
    {type: 'date', label: '日期'},
    {type: 'checkbox', label: '复选框'},
    {type: 'url', label: '链接'},
    {type: 'email', label: '邮箱'},
    {type: 'phone', label: '电话'},
    {type: 'text', label: '文本'},
    {type: 'createdTime', label: '创建时间'},
    {type: 'createdBy', label: '创建人'},
    {type: 'updatedTime', label: '最后更新时间'},
    {type: 'updatedBy', label: '更新人'},
]

export function isTitleProperty(propertyId: string): boolean {
    return propertyId === Constants.titleColumnId
}

/** Sortable/ comparable scalar for a card property, mirroring sortCards(). */
export function sortValueOf(card: Card, template: IPropertyTemplate, usersById: Record<string, {username: string}>): string | number {
    const raw = card.fields.properties[template.id] || ''
    switch (template.type) {
    case 'number':
        return raw === '' ? '' : Number(raw)
    case 'date': {
        const dp = createDatePropertyFromString(raw)
        return dp.from ?? dp.to ?? ''
    }
    case 'createdTime':
        return card.createAt
    case 'updatedTime':
        return card.updateAt
    case 'createdBy':
        return usersById[card.createdBy]?.username || ''
    case 'updatedBy':
        return usersById[card.modifiedBy]?.username || ''
    case 'select':
        return template.options.find((o) => o.id === raw)?.value || ''
    case 'multiSelect':
        return template.options.find((o) => o.id === parseArrayValue(raw)[0])?.value || ''
    case 'person':
        return usersById[raw as string]?.username || ''
    default:
        return typeof raw === 'string' ? raw : ''
    }
}
